import { useMemo } from 'react';
import BigNumber from 'bignumber.js';
import clsx from 'clsx';
import dayjs from '@/lib/dayjs';
import { useNavigate, useQueryState, useQuery, useMutation, useI18n } from '@ywwwtseng/react-kit';
import {
  Typography,
} from '@ywwwtseng/tma-react-kit';
import * as schema from '@/db/schema';
import * as web3 from '@ywwwtseng/web3';
import {
  type FormElement,
  type FormValues,
  Form,
  useForm,
  validator,
} from '@/lib/react-form';
import { CreateGiftConfirm } from '@/components/create-gift/CreateGiftConfirm';
import { SelectChat } from '@/components/common/SelectChat';
import { SelectToken } from '@/components/common/SelectToken';
import { useToken } from '@/hooks/useToken';
import { useChat } from '@/hooks/useChat';
import { useUserBalance } from '@/hooks/useUserBalance';
import { useBlockedWhenServiceUpdated } from '@/hooks/useBlockedWhenServiceUpdated';
import type { GiftMetadata, GiftMetadataSettings } from '@/types';
import { GIFT_TYPES, AMOUNT_TYPES, MOONINI_TOKEN_ID, ONE_MOONINI } from '@/constants';
import * as utils from '@/utils';

export function CreateNormalGiftForm() {
  const { t } = useI18n();
  const createGift = useMutation('gift:create');
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const navigate = useNavigate();

  const form = useForm({
    values: {
      default: {
        image: '',
        description: '',
        tg_chat_id: null,
        amount_type: 'fixed',
        total_amount: '',
        gift_amount: '',
        claim_amount: '',
        token_id: null,
        validity_period: '1',
      },
    },
  });

  const token = useToken(form.values.token_id as string | undefined);
  const chat = useChat(form.values.tg_chat_id as string | undefined);

  const minRequiredUsd = useMemo(() => {
    if (!chat) {
      return new BigNumber(0);
    }
    return new BigNumber(chat.member_count).multipliedBy(0.1).multipliedBy(0.01);
  }, [chat]);

  const tokenSymbol = token?.symbol;

  const { data: gift_fee } = useQuery<{
    platform_fee_percent: number;
    chat_fee_percent: number;
  }>('gift_fee', {
    params: {
      chat_id: form.values.tg_chat_id as string,
      tg_username: me?.tg_username as string,
    },
    enabled: !!form.values.tg_chat_id && !!me?.tg_username,
    showLoading: true,
  });

  const isChatFeeZero = useMemo(() => {
    return gift_fee?.chat_fee_percent === 0;
  }, [gift_fee]);

  const total_amount = useMemo(() => {
    return utils.gift.calculateGiftTotalAmount({
      amount_type: form.values.amount_type as string,
      claim_amount: form.values.claim_amount as string,
      gift_amount: form.values.gift_amount as string,
      total_amount: form.values.total_amount as string,
    });
  }, [form.values]);

  const price = token?.usd_price;

  const totalPrice = useMemo(() => {
    if (!price || !total_amount) {
      return new BigNumber(0);
    }

    return new BigNumber(price).multipliedBy(total_amount);
  }, [price, total_amount]);

  const final_payable = useMemo(() => {
    if (!gift_fee || !token || !total_amount) {
      return '';
    }

    const { final_payable } = utils.gift.fees.calculate({
      fees: {
        platform_fee_percent: gift_fee.platform_fee_percent as number,
        chat_fee_percent: gift_fee.chat_fee_percent as number,
      },
      amount: web3.utils.parseUnits(total_amount, token.decimals).toString(),
    });

    return web3.utils.formatUnits(final_payable, token.decimals).toString();
  }, [gift_fee, total_amount, token]);

  const calculated = useMemo(() => {
    return {
      final_payable,
    };
  }, [final_payable]);


  const minAmount = utils.token.calculateAmountForUsd(price, 0.01);

  const balance = useUserBalance(token);

  const elements = useMemo<{ row: FormElement[] }[]>(() => {
    const tokenBalance = token
      ? t('common.gift_balance', {
        balance: balance.formatted,
      })
      : null;

    const description = form.values.description as string;

    return [
      {
        className: '!mb-0',
        row: [
          {
            ui: 'custom',
            name: 'tg_chat_id',
            validate: validator.required({
              message: t('validation.required', {
                name: t('gift.tg_chat'),
              }),
            }),
            render: ({ values, setValue }) => {
              return (
                <SelectChat
                  value={values.tg_chat_id as string | null}
                  onChange={(value) => {
                    setValue('tg_chat_id', value);
                  }}
                />
              );
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'custom',
            name: 'add_tg_chat_hint',
            render: () => {
              return (
                <div
                  onClick={() => {
                    navigate('AddChat');
                  }}
                >
                  <Typography
                    className="underline cursor-pointer"
                    size="1"
                    color="#777777"
                    i18n="gift.add_tg_chat_hint"
                  />
                </div>
              );
            },
          },
        ],
      },
      {
        className: '!mb-0',
        row: [
          {
            ui: 'custom',
            name: 'token_id',

            validate: validator.required({
              message: t('validation.required', {
                name: t('common.token'),
              }),
            }),
            render: ({ values, setValue }) => {
              return (
                <SelectToken
                  disabled={!form.values.tg_chat_id || !gift_fee}
                  value={values.token_id as string | null}
                  onChange={(value) => {
                    setValue('token_id', value);
                  }}
                />
              );
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'custom',
            name: 'total_amount_symbol_hint',
            render: () => {
              return (
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    navigate('AddToken');
                  }}
                >
                  <Typography
                    className="underline"
                    size="1"
                    color="#777777"
                    i18n="gift.total_amount_symbol_hint"
                  />
                </div>
              );
            },
          },
        ],
      },
      {
        row: [
          {
            name: 'amount_type',
            ui: 'select',
            width: 'fit-content',
            props: {
              options: [
                {
                  name: t('amount_type.fixed'),
                  key: AMOUNT_TYPES.FIXED,
                },
                {
                  name: t('amount_type.random'),
                  key: AMOUNT_TYPES.RANDOM,
                },
              ],
            },
          },
          {
            ui: 'amount',
            dependencies: ['amount_type'],
            label:
              form.values.amount_type === AMOUNT_TYPES.FIXED
                ? t('gift.claim_amount')
                : t('gift.total_amount'),
            name:
              form.values.amount_type === AMOUNT_TYPES.FIXED
                ? 'claim_amount'
                : 'total_amount',
            validate: [
              validator.gt(0, {
                message: t('validation.gt', {
                  name:
                    form.values.amount_type === AMOUNT_TYPES.FIXED
                      ? t('gift.claim_amount')
                      : t('gift.total_amount'),
                  value: 0,
                }),
              }),
            ],
            props: {
              disabled: !token,
              placeholder: '0',
              decimal: token ? Number(token.decimals) : 0,
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'amount',
            label: t('gift.gift_amount'),
            validateType: 'onChange',
            name: 'gift_amount',
            validate: [
              validator.gt(0, {
                message: t('validation.gt', {
                  name: t('gift.gift_amount'),
                  value: 0,
                }),
              }),
              validator.lte(
                form.values.amount_type === AMOUNT_TYPES.FIXED ? 200000 : 10000,
                {
                  message: t('validation.lte', {
                    name: t('gift.gift_amount'),
                    value: Number(
                      form.values.amount_type === AMOUNT_TYPES.FIXED
                        ? 200000
                        : 10000
                    ).toLocaleString(),
                  }),
                }
              ),
              () => {
                const amount_type = form.values.amount_type as string;

                if (amount_type === AMOUNT_TYPES.FIXED) {
                  const claim_amount = form.values.claim_amount as string;
                  if (typeof minAmount === 'string' && new BigNumber(claim_amount).gt(0) && new BigNumber(claim_amount).lt(minAmount)) {

                    return t('validation.gt_0_01_usd', {
                      value: utils.token.displayAmount(minAmount, { showTinyAmount: false }),
                    });
                  }
                } else if (amount_type === AMOUNT_TYPES.RANDOM) {
                  const total_amount = form.values.total_amount as string;
                  const gift_amount = form.values.gift_amount as string;
                  if (
                    typeof minAmount === 'string'
                    && new BigNumber(gift_amount).gt(0)
                    && new BigNumber(total_amount).gt(0)
                    && new BigNumber(total_amount).dividedBy(gift_amount).lt(minAmount)
                  ) {
                    return t('validation.gt_0_01_usd', {
                      value: utils.token.displayAmount(minAmount, { showTinyAmount: false }),
                    });
                  }
                }

                return undefined;
              }
            ],
            props: {
              placeholder: '0',
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'custom',
            name: 'final_payable',
            validateType: 'onChange',
            validate: [
              () => {
                if (
                  final_payable &&
                  token &&
                  utils.token.isInsufficient({
                    balance: balance.value,
                    amount: final_payable as string,
                    decimals: token.decimals,
                  })
                ) {
                  return t('common.insufficient_balance');
                }
              },
              () => {
                // 如果使用 $MOONINI 發送禮物，錢包至少保留 5 moonini
                if (
                  token?.id === MOONINI_TOKEN_ID &&
                  final_payable &&
                  balance.totalValue
                ) {
                  // final_payable 是格式化後的值，需要轉換為 base units
                  const finalPayableBaseUnits = web3.utils.parseUnits(final_payable, token.decimals).toString();
                  const minReserveBaseUnits = new BigNumber(5).multipliedBy(ONE_MOONINI).toFixed();
                  const requiredBalance = new BigNumber(finalPayableBaseUnits).plus(minReserveBaseUnits);

                  const balanceBN = new BigNumber(balance.totalValue);
                  if (balanceBN.lt(requiredBalance)) {
                    return t('message.moonini_minimum_reserve_required', {
                      min_reserve: '5',
                    });
                  }
                }
              },
              () => {
                const claim_amount_or_total_amount = form.values.amount_type === AMOUNT_TYPES.FIXED ? form.values.claim_amount as string : form.values.total_amount as string;
                const gift_amount = form.values.gift_amount as string;

                if (!claim_amount_or_total_amount || !gift_amount || !totalPrice || isChatFeeZero) {
                  return undefined;
                }

                if (totalPrice.lt(10)) {
                  return t('message.total_price_lt_10_usd');
                }

                if (chat?.member_count && totalPrice.lt(minRequiredUsd)) {
                  return t('message.gift_total_amount_too_low_for_chat_members', {
                    member_count: chat?.member_count,
                    min_required_usd: minRequiredUsd.toFixed(),
                  });
                }
              },
            ],
            render: () => {
              return (
                <div className="w-full h-10 pl-2 pr-1 bg-form-element/30 rounded-lg flex items-center justify-between">
                  <Typography size="2" i18n="gift.payable_amount" />
                  <Typography
                    size="2"
                    className={
                      final_payable &&
                        token &&
                        utils.token.isInsufficient({
                          balance: balance.value,
                          amount: final_payable as string,
                          decimals: token.decimals,
                        })
                        ? 'text-destructive'
                        : ''
                    }
                  >
                    {final_payable && token
                      ? `${new BigNumber(final_payable).toFixed()} ${tokenSymbol}`
                      : ''}
                  </Typography>
                </div>
              );
            },
            description: (
              <div className="flex flex-col gap-1">
                {token && tokenBalance && (
                  <Typography size="1" color="rgba(255,255,255,0.54)">
                    {tokenBalance} {tokenSymbol}
                  </Typography>
                )}
                {token?.id === MOONINI_TOKEN_ID && final_payable && (
                  <Typography size="1" color="rgba(255,255,255,0.54)">
                    {t('gift.moonini_reserve_note', { min_reserve: '5' })}
                  </Typography>
                )}
              </div>
            ),
          },
        ],
      },
      {
        row: [
          {
            ui: 'amount',
            label: t('gift.validity_period'),
            name: 'validity_period',
            validate: [
              validator.gt(0, {
                message: t('validation.gt', {
                  name: t('gift.validity_period'),
                  value: 0,
                }),
              }),
            ],
            props: {
              placeholder: '0',
            },
          },
        ],
      },
      {
        row: [
          {
            ui: 'textarea',
            name: 'description',
            validateType: 'onChange',
            validate: [
              validator.required({
                message: t('validation.required', {
                  name: t('gift.description'),
                }),
              }),
              validator.maxLength({
                message: t('validation.max_length', {
                  name: t('gift.description'),
                  max: 500,
                }),
                max: 500,
              }),
            ],
            props: {
              placeholder: t('gift.description_placeholder'),
            },
            description: (
              <Typography
                size="1"
                className={clsx(
                  'ml-auto',
                  description.length > 500
                    ? 'text-destructive'
                    : 'text-placeholder'
                )}
              >
                {`${String(description.length)}/500`}
              </Typography>
            ),
          },
        ],
      },
    ];
  }, [form.values, token, balance, total_amount, minAmount, final_payable, chat, minRequiredUsd, isChatFeeZero, totalPrice, t]);

  const { mutate, isLoading } = useBlockedWhenServiceUpdated(async (values: FormValues) => {
    if (!token || !me) {
      return;
    }

    const formData = new FormData();

    formData.append('type', GIFT_TYPES.NORMAL);
    formData.append('amount_type', values.amount_type as string);
    formData.append('description', values.description as string);
    formData.append('tg_chat_id', values.tg_chat_id as string);

    formData.append(
      'amount',
      web3.utils.parseUnits(total_amount, token.decimals).toString()
    );
    formData.append('token_id', values.token_id as string);
    if (token.network) {
      formData.append('network', token.network);
    }
    formData.append('language_code', me.language_code ?? 'en');

    const validity_period = Number(values.validity_period);
    if (validity_period > 0) {
      formData.append(
        'end_at',
        dayjs().add(validity_period, 'day').toISOString()
      );
    }

    const metadata = values.metadata as unknown as GiftMetadata;
    const settings: GiftMetadataSettings =
      values.amount_type === AMOUNT_TYPES.FIXED
        ? [
          {
            ca: web3.utils
              .parseUnits(values.claim_amount as string, token.decimals)
              .toString(),
          },
        ]
        : utils.gift.lucky
          .gaussian(
            Number(web3.utils.parseUnits(total_amount, token.decimals)),
            Number(values.gift_amount)
          )
          .map((ca) => ({
            ca,
          }));

    formData.append(
      'metadata',
      JSON.stringify({
        ...metadata,
        inline_keyboard_text: 'Claim NOW 🧧',
        settings,
      })
    );

    const result = await createGift.mutate(formData);

    if (result.ok) {
      navigate('Home');
    }
  });

  return (
    <Form
      {...form}
      calculated={calculated}
      elements={elements}
      name="gift"
      isLoading={isLoading}
      confirm={({ isLoading, onConfirm }) => (
        <CreateGiftConfirm
          amount={total_amount}
          token_id={token?.id as string}
          platform_fee_percent={gift_fee?.platform_fee_percent as number}
          chat_fee_percent={gift_fee?.chat_fee_percent as number}
          isLoading={isLoading}
          onConfirm={onConfirm}
        />
      )}
      submit={{
        label: t('common.submit'),
      }}
      onSubmit={mutate}
    />
  );
}
